/** @jsx jsx */
import { Fragment, useEffect, useState } from "react"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import { Flex, jsx, Styled } from "theme-ui"

import Variant from "@components/Variant"
import Border from "@src/components/Border"
import useLocaleContext from "@src/hooks/useLocaleContext"

import Loading from "../DepositTerms/Loading/Loading"
import DateRangePicker from "./DateRangePicker"
import {
  formatDate,
  formatNumber,
  formatTermPeriod,
} from "../DepositTerms/helper"

export const fragment = graphql`
  fragment ContentfulDepositTermsHistoryBlock on ContentfulDepositTermsHistoryBlock {
    id
    title
    depositTermsUrl
    depositTermsHistoryUrl
  }
`

const INITIAL_ROW_COUNT = 15

const toTermKey = ({ type, term_period }) => `${type}-${term_period}`

const withFromDate = (url, from) => {
  const rangedUrl = new URL(url, window.location.href)
  if (from) rangedUrl.searchParams.set("from", from)
  return rangedUrl.toString()
}

const fetchJson = url =>
  fetch(url).then(response => {
    if (!response.ok) throw new Error(response.statusText)
    return response.json()
  })

// History only lists the changes per term, so each row is rebuilt as the full
// set of rates in effect from that date. History is always fetched up to today,
// which makes today's rate correct for terms without any changes in it.
const rateBeforeHistory = term =>
  term.changes.length > 0
    ? term.changes[0].interest_rate_before
    : term.interest_rate ?? null

const toOldestFirst = changes =>
  [...changes].reverse().sort((a, b) => a.date.localeCompare(b.date))

const toRateSheets = (currentTerms, historyTerms) => {
  const terms = new Map()

  currentTerms.forEach(term =>
    terms.set(toTermKey(term), { ...term, changes: [] })
  )
  historyTerms.forEach(term =>
    terms.set(toTermKey(term), {
      ...terms.get(toTermKey(term)),
      ...term,
      changes: toOldestFirst(term.changes),
    })
  )

  const allColumns = [...terms.values()].sort((a, b) => {
    if (a.term_period === null) return -1
    if (b.term_period === null) return 1
    return a.term_period - b.term_period
  })

  const dates = [
    ...new Set(historyTerms.flatMap(term => term.changes.map(c => c.date))),
  ].sort((a, b) => b.localeCompare(a))

  const toCell = (term, date) => {
    const appliedChanges = term.changes.filter(c => c.date <= date)
    const latestChange = appliedChanges[appliedChanges.length - 1]
    return {
      key: toTermKey(term),
      rate: latestChange
        ? latestChange.interest_rate_after
        : rateBeforeHistory(term),
      changed: latestChange?.date === date,
    }
  }

  const columns = allColumns.filter(term =>
    dates.some(date => toCell(term, date).rate !== null)
  )
  const rows = dates.map(date => ({
    date,
    cells: columns.map(term => toCell(term, date)),
  }))

  return { columns, rows }
}

export default function DepositTermsHistory({
  title,
  depositTermsUrl,
  depositTermsHistoryUrl,
}) {
  const locale = useLocaleContext()

  const [currentTerms, setCurrentTerms] = useState(null)
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [columns, setColumns] = useState([])
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState(null)
  const [showAll, setShowAll] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const today = formatDate(new Date())

  useEffect(() => {
    fetchJson(depositTermsUrl)
      .then(current => setCurrentTerms(current.data))
      .catch(() => {
        setHasError(true)
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!currentTerms) return undefined

    let isStale = false
    setIsLoading(true)
    setHasError(false)
    fetchJson(withFromDate(depositTermsHistoryUrl, fromDate))
      .then(history => {
        if (isStale) return
        const rateSheets = toRateSheets(currentTerms, history.data)
        setColumns(rateSheets.columns)
        setRows(rateSheets.rows)
        setMeta(history.meta)
        setShowAll(false)
        setIsLoading(false)
      })
      .catch(() => {
        if (isStale) return
        setHasError(true)
        setIsLoading(false)
      })

    return () => {
      isStale = true
    }
  }, [currentTerms, fromDate])

  const selectedFrom = fromDate || meta?.from
  const selectedTo = toDate || meta?.to

  const applyRange = range => {
    setFromDate(range.from)
    setToDate(range.to)
    setShowAll(false)
  }

  const formatRate = rate =>
    rate === null ? "–" : formatNumber(locale, rate, { style: "percent" })

  const rowsInRange = rows.filter(row => row.date <= selectedTo)
  const visibleRows = showAll
    ? rowsInRange
    : rowsInRange.slice(0, INITIAL_ROW_COUNT)

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex sx={{ justifyContent: "center", width: "100%" }}>
          <Loading />
        </Flex>
      )
    }

    if (hasError) {
      return (
        <p sx={{ fontSize: 3, textAlign: "center" }}>
          Just nu verkar vår räntehistorik inte kunna visas, ladda om sidan
          eller försök igen senare.
        </p>
      )
    }

    if (rowsInRange.length === 0) {
      return (
        <p sx={{ fontSize: 3, textAlign: "center" }}>
          Inga ränteändringar har gjorts mellan {selectedFrom} och {selectedTo}.
        </p>
      )
    }

    return (
      <Fragment>
        <div sx={{ overflowX: "auto", width: "100%" }}>
          <table>
            <thead>
              <tr>
                <th scope="col">Datum</th>
                {columns.map(term => (
                  <th key={toTermKey(term)} scope="col">
                    {formatTermPeriod(term)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(row => (
                <tr key={row.date}>
                  <th scope="row">{row.date}</th>
                  {row.cells.map(cell => (
                    <td
                      key={cell.key}
                      sx={{ fontWeight: cell.changed ? "bold" : "regular" }}
                    >
                      {formatRate(cell.rate)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Variant as="p" sx={{ textAlign: "left", width: "100%", fontSize: 1 }}>
          Fetstil markerar de räntor som ändrades det datumet.
        </Variant>
        {rowsInRange.length > INITIAL_ROW_COUNT && (
          <Flex sx={{ justifyContent: "center", mt: 12 }}>
            <button
              onClick={() => setShowAll(!showAll)}
              sx={{ variant: "button.secondary" }}
            >
              {showAll ? "Visa färre" : "Visa alla"}
            </button>
          </Flex>
        )}
      </Fragment>
    )
  }

  return (
    <Fragment>
      <Variant
        variants={["container", "containerSpacing"]}
        d="grid"
        as="section"
        gap={0}
        sx={{
          gridTemplateColumns: "minmax(0, 1fr)",
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: "left",
          fontSize: 3,
          lineHeight: "1.25",
          p: {
            margin: 0,
            "&:empty": {
              display: "none",
            },
          },
          table: {
            borderCollapse: "collapse",
            width: "100%",
            marginBottom: [5],
            fontSize: [1, 2],
          },
          "th, td": {
            px: [4, 5],
            py: [4, 5],
            whiteSpace: "nowrap",
            textAlign: "right",
          },
          th: {
            fontWeight: "regular",
          },
          "th:first-of-type": {
            position: "sticky",
            left: 0,
            textAlign: "left",
            backgroundColor: "white",
          },
          "thead th": {
            color: "grey.dark",
            borderBottom: "2px solid",
            borderBottomColor: "grey.light",
          },
          "tbody tr:nth-of-type(2n) > *": {
            backgroundColor: "rgba(245, 245, 245, 1)",
          },
        }}
      >
        <Styled.h2 sx={{ textAlign: "center", marginBottom: [9, 13] }}>
          {title}
        </Styled.h2>
        <Variant>
          {meta && (
            <div sx={{ width: "100%", marginBottom: [7, 9] }}>
              <DateRangePicker
                from={selectedFrom}
                to={selectedTo}
                min={meta.available_from}
                max={today}
                onApply={applyRange}
              />
            </div>
          )}
          {renderContent()}
        </Variant>
      </Variant>
      <Border />
    </Fragment>
  )
}

DepositTermsHistory.typeName = "ContentfulDepositTermsHistoryBlock"

DepositTermsHistory.defaultProps = {
  title: "",
  depositTermsUrl: "",
  depositTermsHistoryUrl: "",
}

DepositTermsHistory.propTypes = {
  title: PropTypes.string.isRequired,
  depositTermsUrl: PropTypes.string.isRequired,
  depositTermsHistoryUrl: PropTypes.string.isRequired,
}
